<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import SecurityUserAvatar from "@/components/security/SecurityUserAvatar.vue";
import { saveUserListState, restoreUserListState } from "./securityUserListState";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import Notification from "@/utils/plugins/NotificationService";
import EmployeeService from "@/api/services/employee.service";
import ResourceService from "@/api/services/resource.service";

const page = ref(1), itemsPerPage = ref(10);
const router = useRouter();
const filters = ref({ search: "", department: "", status: "active", role: "", employeeType: "" });

const users = ref([]);
const totalResults = ref(0);
const departments = ref([]);
const jobs = ref([]);

const tones = ["#b88761", "#96634d", "#a69964", "#628db2", "#9d705b", "#bb9564", "#6d95b3", "#b77867", "#8d7658", "#ad668b", "#9871ad", "#4f4f4f"];
const toneFor = (id) => tones[id % tones.length];
const initialsOf = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const buildParams = () => {
  const params = { page: page.value, itemsPerPage: itemsPerPage.value, "order[fullName]": "ASC" };
  if (filters.value.search) params.orSearch_fullName = filters.value.search;
  if (filters.value.department) params["job.department.name"] = filters.value.department;
  if (filters.value.role) params["job.name"] = filters.value.role;
  if (filters.value.employeeType) params.employeeType = filters.value.employeeType;
  if (filters.value.status) params.active = filters.value.status === "active" ? 1 : 0;
  return params;
};

const getUsers = () => {
  EmployeeService.getEmployees(buildParams()).then((res) => {
    if (!res) return;
    users.value = (res.data["hydra:member"] ?? []).map((employee) => ({
      id: employee.id,
      photo: employee.photo,
      initials: initialsOf(employee.fullName),
      name: employee.fullName,
      email: employee.email,
      department: employee.job?.department?.name ?? "—",
      role: employee.job?.name ?? "—",
      status: employee.active ? "active" : "inactive",
      lastLogin: "—",
      special: null,
      tone: toneFor(employee.id),
    }));
    totalResults.value = res.data["hydra:totalItems"] ?? users.value.length;
  });
};

const getDepartments = () => {
  ResourceService.getDepartments().then((res) => {
    if (!res) return;
    departments.value = (res.data["hydra:member"] ?? []).map((d) => d.name);
  });
};

const getJobs = () => {
  ResourceService.getJobs().then((res) => {
    if (!res) return;
    jobs.value = (res.data["hydra:member"] ?? []).map((j) => j.name);
  });
};

const search = () => { page.value = 1; getUsers(); };

const pending = (action) => Notification.info(`${action} estará disponible próximamente.`, 3500);
const openPermissions = (user) => {
  saveUserListState("internal", { filters: filters.value, page: page.value, itemsPerPage: itemsPerPage.value });
  router.push({ name: "security-user-permissions", query: { user: user.id } });
};

onMounted(() => {
  const stored = restoreUserListState("internal");
  if (stored) {
    filters.value = { ...filters.value, ...stored.filters };
    page.value = stored.page || 1;
    itemsPerPage.value = stored.itemsPerPage || 10;
  }
  getUsers();
  getDepartments();
  getJobs();
});
</script>

<template>
  <section class="internal-users">
    <HeaderTitle title="Seguridad" />
    <SecuritySectionTitle title="Usuarios internos" />
    <el-card class="card-search filters" body-style="padding: 0">
      <div class="row">
        <div class="col-12 col-md-6 col-xl-3 form-item">
          <label>Nombre o email</label>
          <el-input v-model="filters.search" class="w-100" placeholder="Buscar por nombre o email"
            @keyup.enter="search" />
        </div>
        <div class="col-12 col-md-6 col-xl-3 form-item">
          <label>Departamento/equipo</label>
          <el-select v-model="filters.department" class="w-100" placeholder="Todos" clearable filterable
            @change="search">
            <el-option v-for="item in departments" :key="item" :label="item" :value="item" /></el-select>
        </div>
        <div class="col-12 col-md-6 col-xl-3 form-item">
          <label>Estado</label>
          <el-select v-model="filters.status" class="w-100" placeholder="Todos" clearable @change="search">
            <el-option label="Activo" value="active" /><el-option label="Inactivo" value="inactive" /></el-select>
        </div>
        <div class="col-12 col-md-6 col-xl-3 form-item">
          <label>Puesto/Rol</label>
          <el-select v-model="filters.role" class="w-100" placeholder="Todos" clearable filterable @change="search">
            <el-option v-for="item in jobs" :key="item" :label="item" :value="item" /></el-select>
        </div>
        <div class="col-12 col-md-6 col-xl-3 form-item">
          <label>Tipo empleado</label>
          <el-select v-model="filters.employeeType" class="w-100" placeholder="Todos" clearable @change="search">
            <el-option label="Empleado" value="employee" />
            <el-option label="Becario" value="intern" />
            <el-option label="Colaborador externo" value="external_collaborator" /></el-select>
        </div>
        <div class="col-12 col-md-6 col-xl-3 actions">
          <button type="button" class="btn btn_red" @click="pending('Crear usuario interno')">NUEVO</button>
          <button type="button" class="btn btn_yellow btn-search" @click="search">BUSCAR</button>
        </div>
      </div>
    </el-card>
    <div class="table table-not-mobile">
      <table>
        <thead>
          <tr>
            <th><img src="@/assets/icons/user.svg" alt="" /> Nombre</th>
            <th><img src="@/assets/icons/email-icon.svg" alt="" /> Email</th>
            <th><img src="@/assets/icons/mouse-icon.svg" alt="" /> Departamento</th>
            <th><img src="@/assets/icons/medal-icon.svg" alt="" /> Puesto/Rol</th>
            <th><svg class="shield-icon" viewBox="0 0 24 24">
                <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
                <path d="m8.8 11.7 2.2 2.2 4.5-4.5" />
              </svg> Estado</th>
            <th><img src="@/assets/icons/clockify-blue.svg" alt="" /> Último login</th>
            <th><svg class="shield-icon" viewBox="0 0 24 24">
                <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
                <path d="m8.8 11.7 2.2 2.2 4.5-4.5" />
              </svg> Permisos especiales</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id" class="pointer" @click="openPermissions(user)">
            <td>
              <div class="person"><SecurityUserAvatar :photo="user.photo" :initials="user.initials" :tone="user.tone" />{{ user.name }}
              </div>
            </td>
            <td>{{ user.email }}</td>
            <td>{{ user.department }}</td>
            <td>{{ user.role }}</td>
            <td><b class="status" :class="user.status">{{ user.status === 'active' ? 'Activo' : 'Inactivo' }}</b></td>
            <td>{{ user.lastLogin }}</td>
            <td class="center">
              <span v-if="user.special === null">—</span>
              <svg v-else-if="user.special" class="shield-icon shield-icon--permission" viewBox="0 0 24 24"
                aria-label="Tiene permisos especiales">
                <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
                <path d="m8.8 11.7 2.2 2.2 4.5-4.5" />
              </svg><span v-else>—</span></td>
            <td>
              <div class="row-actions"><font-awesome-layers class="fa-2x" title="Gestionar usuario"
                  @click.stop="openPermissions(user)"><font-awesome-icon icon="circle"
                    style="color:#f7d20e" /><font-awesome-icon icon="plus" class="fa-inverse"
                    transform="shrink-6" /></font-awesome-layers><font-awesome-layers class="fa-2x"
                  title="Desactivar usuario" @click.stop="pending(`Desactivar ${user.name}`)"><font-awesome-icon
                    icon="circle" style="color:red" /><font-awesome-icon icon="times" class="fa-inverse"
                    transform="shrink-6" /></font-awesome-layers></div>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!users.length" class="empty">No se han encontrado usuarios con los filtros seleccionados.</p>
    </div>
    <div class="table-mobile cards"><el-card v-for="user in users" :key="user.id" class="pointer"
        @click="openPermissions(user)">
        <div class="card-head">
          <div class="person"><SecurityUserAvatar :photo="user.photo" :initials="user.initials" :tone="user.tone" /><strong>{{ user.name
          }}</strong></div>
          <div class="row-actions"><button @click.stop="openPermissions(user)">+</button><button class="delete"
              @click.stop="pending(`Desactivar ${user.name}`)">×</button></div>
        </div>
        <p><b>Email:</b>{{ user.email }}</p>
        <p><b>Departamento:</b>{{ user.department }}</p>
        <p><b>Puesto/Rol:</b>{{ user.role }}</p>
        <p><b>Estado:</b><span class="status" :class="user.status">{{ user.status === 'active' ? 'Activo' : 'Inactivo'
        }}</span>
        </p>
        <p><b>Último login:</b>{{ user.lastLogin }}</p>
        <p><b>Permisos especiales:</b>{{ user.special === null ? '—' : (user.special ? 'Sí' : 'No') }}</p>
      </el-card>
      <p v-if="!users.length" class="empty">No se han encontrado usuarios con los filtros seleccionados.</p>
    </div>
    <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" :total="totalResults"
      :page-size-options="[10, 20, 50]" class="pagination" @current-change="getUsers"
      @size-change="page = 1; getUsers()" />
  </section>
</template>

<style lang="scss" scoped>
.internal-users {
  color: #30385d
}

header {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 17px
}

header button {
  background: transparent;
  border: 0;
  color: #474ce6;
  padding: 0;
  width: 28px
}

header svg {
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  width: 28px
}

h1 {
  color: #474ce6;
  font-size: clamp(2rem, 3.3vw, 3.2rem);
  font-weight: 900;
  letter-spacing: -.055em;
  margin: 0 0 7px
}

h1 span {
  font-size: .58em;
  white-space: nowrap
}

header p {
  color: #7c83a2;
  margin: 0
}

.filters {
  background: #fff !important;
  background-image: none !important;
  border: 0;
  border-radius: 10px;
  box-shadow: 0 5px 18px #30385d1a;
}

.filters :deep(.el-card__body) {
  background: #fff !important;
  background-image: none !important
}

.filters .row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  row-gap: 14px
}

.filters .row>[class*="col-"] {
  max-width: none;
  padding-left: 12px;
  padding-right: 12px;
  width: auto
}


.actions {
  align-items: center;
  align-self: start;
  display: flex;
  gap: 14px;
  padding-top: 25px
}

.actions .btn {
  align-items: center;
  display: flex;
  flex: 1;
  height: 44px;
  justify-content: center;
  margin: 0 !important;
  min-width: 0;
  width: auto
}

.actions .btn-search {
  color: #474ce6 !important
}

.actions .btn-search:hover,
.actions .btn-search:focus-visible {
  color: #474ce6 !important
}

.person {
  align-items: center;
  display: flex;
  gap: 10px
}

.table table {
  min-width: 1120px
}

.table th {
  text-transform: uppercase
}

.table th img {
  height: 20px;
  margin-right: 4px;
  width: 20px
}

.table td {
  vertical-align: middle
}

.table th:nth-child(7),
.table td:nth-child(7) {
  padding-left: 8px;
  padding-right: 8px;
  text-align: center;
  width: 105px
}

.shield-icon {
  display: inline-block;
  fill: none;
  stroke: #474ce6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  vertical-align: middle;
  height: 20px;
  margin-right: 4px;
  width: 20px
}

.shield-icon--permission {
  height: 24px;
  margin: 0;
  width: 24px
}

.status {
  border: 1px solid;
  border-radius: 15px;
  font-size: .7rem;
  font-weight: 400;
  padding: 3px 10px
}

.active {
  background: #effbf4;
  border-color: #c9eed9;
  color: #279653
}

.inactive {
  background: #fff7ef;
  border-color: #ffe0ba;
  color: #fb6b21
}

.center {
  text-align: center
}

.row-actions {
  display: flex;
  gap: 8px;
  justify-content: center
}

.row-actions :deep(.fa-layers) {
  cursor: pointer
}

.row-actions button {
  background: #f7c800;
  border: 0;
  border-radius: 50%;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  height: 19px;
  line-height: 1;
  padding: 0;
  width: 19px
}

.row-actions .delete {
  background: #f9151c
}

.cards {
  display: none
}

.pagination {
  display: flex;
  justify-content: center;
  margin: 16px 0 22px
}

.empty {
  color: #7c83a2;
  text-align: center
}

@media(min-width:768px) {
  .table-mobile {
    display: none
  }
}

@media(max-width:767px) {
  .table-not-mobile {
    display: none
  }

  header {
    align-items: flex-start
  }

  h1 span {
    display: block;
    font-size: .65em;
    margin-top: 4px
  }

  .filters .row {
    display: grid;
    grid-template-columns: 1fr;
    padding: 15px 13px
  }

  .filters .row>[class*="col-"] {
    padding-left: 0;
    padding-right: 0
  }

  .actions {
    padding-top: 4px
  }

  .actions .btn {
    width: auto
  }

  .cards {
    display: grid;
    gap: 12px
  }

  .cards :deep(.el-card__body) {
    padding: 14px
  }

  .card-head {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px
  }

  .cards p {
    display: grid;
    gap: 7px;
    grid-template-columns: 130px minmax(0, 1fr);
    margin: 8px 0;
    word-break: break-word
  }

  .cards p b {
    color: #474ce6
  }

  .pagination {
    max-width: 100%;
    overflow: hidden
  }
}

.security-subtitle {
  align-items: center;
  display: flex;
  margin-bottom: 10px
}

.security-subtitle svg {
  fill: none;
  stroke: #474ce6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  width: 30px
}

.security-subtitle .title-text {
  color: #474ce6;
  font-size: 2.5em;
  margin-left: 1rem
}
</style>
